import React, { useCallback, useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Warehouse,
  Search,
  Plus,
  RotateCcw,
  History,
  XCircle,
  BarChart2,
  User,
  Clock,
  ChevronDown,
  ChevronUp,
  Package,
  CheckCircle,
  AlertTriangle,
  ScanLine,
} from "lucide-react";
import { format } from "date-fns";
import Loader from "../../loader/Loader";
import AddWarehouseReceiving from "./AddWarehouseReceiving";
import Pagination from "../Pagination";
import { domain } from "../../../security";
import { selectUserID } from "../../../redux/IchthusSlice";

const formatDate = (d) => (d ? format(new Date(d), "MMM dd, yyyy HH:mm") : "—");
const formatDateShort = (d) => (d ? format(new Date(d), "MMM dd, yyyy") : "—");

const STATUS_CONFIG = {
  Received: { bg: "bg-green-50", text: "text-green-700", dot: "bg-green-500" },
  "Partially Received": {
    bg: "bg-amber-50",
    text: "text-amber-700",
    dot: "bg-amber-400",
  },
  Voided: { bg: "bg-red-50", text: "text-red-600", dot: "bg-red-400" },
  Completed: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    dot: "bg-emerald-500",
  },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || {
    bg: "bg-slate-100",
    text: "text-slate-600",
    dot: "bg-slate-400",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wide ${cfg.bg} ${cfg.text}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {status}
    </span>
  );
};

// ── VOID MODAL ────────────────────────────────────────────────────────────────
const VoidModal = ({ isOpen, onClose, onConfirm, receivingNumber }) => {
  const [reason, setReason] = useState("");
  const currentUserName = ""; // pull from redux if needed

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <h3 className="font-black text-slate-800 text-base mb-1">
          Void Receiving
        </h3>
        <p className="text-xs text-slate-400 mb-1 font-mono">
          {receivingNumber}
        </p>
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          ⚠ Voiding will reverse the received quantities on the Warehouse
          Request.
        </p>
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for void..."
          className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-100 focus:border-red-400 resize-none"
        />
        <div className="flex gap-2 mt-4">
          <button
            onClick={onClose}
            className="flex-1 py-2 text-sm font-bold text-slate-500 hover:bg-slate-50 rounded-xl border border-slate-200 transition"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm(reason);
              setReason("");
            }}
            disabled={!reason.trim()}
            className="flex-1 py-2 text-sm font-bold text-white bg-red-500 hover:bg-red-600 rounded-xl disabled:opacity-40 transition"
          >
            Confirm Void
          </button>
        </div>
      </div>
    </div>
  );
};

// ── EXPANDED ITEMS ─────────────────────────────────────────────────────────────
const ExpandedItems = ({ items }) => (
  <div className="px-4 md:px-6 pb-4 pt-2">
    <div className="bg-slate-50 rounded-xl border border-slate-100 overflow-x-auto">
      <table className="w-full min-w-[400px] text-xs">
        <thead>
          <tr className="bg-slate-100 text-slate-500 uppercase tracking-wide">
            <th className="px-4 py-2 text-left">#</th>
            <th className="px-4 py-2 text-left">Product</th>
            <th className="px-4 py-2 text-right">Received Qty</th>
            <th className="px-4 py-2 text-right">Serials Scanned</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {(items || []).map((item, i) => (
            <tr key={i} className="hover:bg-white transition">
              <td className="px-4 py-2 text-slate-400 font-bold">{i + 1}</td>
              <td className="px-4 py-2 font-semibold text-slate-700">
                {item.productName}
                {item.itemCode && (
                  <span className="ml-1 text-slate-400 font-mono text-[10px]">
                    ({item.itemCode})
                  </span>
                )}
              </td>
              <td className="px-4 py-2 text-right font-black text-indigo-700">
                {item.quantityReceived}
              </td>
              <td className="px-4 py-2 text-right font-bold text-slate-600">
                {item.serialCount ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
const AllWarehouseReceiving = () => {
  const currentUserID = useSelector(selectUserID);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null); // { id, receivingNumber }

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${domain}/api/WarehouseReceiving`);
      setData(res.data || []);
    } catch {
      toast.error("Failed to load Warehouse Receivings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = data.filter((item) => {
    const q = searchTerm.toLowerCase();
    const matchSearch =
      (item.receivingNumber || "").toLowerCase().includes(q) ||
      (item.wRNumber || "").toLowerCase().includes(q) ||
      (item.remarks || "").toLowerCase().includes(q);
    const matchStatus = statusFilter === "All" || item.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = filtered.slice(indexOfFirst, indexOfLast);

  const toggleExpand = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleVoid = async (reason) => {
    if (!voidTarget) return;
    try {
      await axios.post(
        `${domain}/api/WarehouseReceiving/void/${voidTarget.id}`,
        {
          voidBy: String(currentUserID),
          reason,
        },
      );
      toast.success("Receiving voided.");
      setVoidTarget(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to void.");
    }
  };

  const statuses = ["All", "Received", "Voided"];

  // Stats
  const totalReceivings = data.length;
  const receivedCount = data.filter((d) => !d.isVoid).length;
  const voidedCount = data.filter((d) => d.isVoid).length;

  return (
    <div className="min-h-screen pb-20 bg-slate-50/40">
      <ToastContainer position="top-right" autoClose={3000} />

      <VoidModal
        isOpen={!!voidTarget}
        onClose={() => setVoidTarget(null)}
        onConfirm={handleVoid}
        receivingNumber={voidTarget?.receivingNumber}
      />

      {isAddModalVisible && (
        <AddWarehouseReceiving
          onClose={() => setIsAddModalVisible(false)}
          onSuccess={() => {
            setIsAddModalVisible(false);
            fetchData();
          }}
        />
      )}

      {/* ── HEADER ── */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-black text-slate-800 flex items-center gap-2">
                <ScanLine className="text-indigo-600" size={22} />
                Warehouse Receiving
              </h1>
              <p className="text-xs text-slate-500 mt-1 hidden sm:block">
                Record and track physical stock received into warehouses.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 w-full md:w-auto">
              <button
                onClick={() => setIsAddModalVisible(true)}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-black px-5 py-2.5 rounded-xl shadow-lg shadow-indigo-200 transition-all"
              >
                <Plus size={18} /> New Receiving
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-4 px-4">
        {/* ── STATS ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
              <BarChart2 size={22} />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Total Receivings
              </p>
              <p className="text-2xl font-black text-slate-800">
                {totalReceivings}
              </p>
            </div>
          </div>

          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
            <div className="p-3 bg-green-50 text-green-600 rounded-xl">
              <CheckCircle size={22} />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Active
              </p>
              <p className="text-2xl font-black text-slate-800">
                {receivedCount}
              </p>
            </div>
          </div>

          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by WRV#, WR#..."
                className="w-full pl-9 pr-4 py-2 text-sm border-none ring-1 ring-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none placeholder:text-slate-400"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </div>

        {/* ── STATUS FILTERS ── */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {statuses.map((s) => (
            <button
              key={s}
              onClick={() => {
                setStatusFilter(s);
                setCurrentPage(1);
              }}
              className={`px-4 py-1.5 rounded-xl text-xs font-black transition-all ${
                statusFilter === s
                  ? "bg-indigo-600 text-white shadow-md shadow-indigo-200"
                  : "bg-white text-slate-500 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              {s}
              {s !== "All" && (
                <span className="ml-1.5 opacity-60">
                  {
                    data.filter((d) =>
                      s === "Received" ? !d.isVoid : d.isVoid,
                    ).length
                  }
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── TABLE ── */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader />
          </div>
        ) : (
          <div className="flex flex-col gap-4 md:gap-0 md:bg-white md:rounded-2xl md:shadow-sm md:border md:border-slate-200 md:overflow-hidden">
            {/* Desktop Header */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase font-black">
              <div className="col-span-1" />
              <div className="col-span-2">WRV Number</div>
              <div className="col-span-2">WR Number</div>
              <div className="col-span-2">Warehouse</div>
              <div className="col-span-2">Received By</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1">Date</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>

            {currentItems.map((item) => (
              <React.Fragment key={item.id}>
                <div className="bg-white rounded-2xl md:rounded-none p-4 md:p-0 shadow-sm border border-slate-100 md:border-0 md:border-b md:last:border-none hover:bg-slate-50/30 transition">
                  {/* Desktop Row */}
                  <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 items-center">
                    <div className="col-span-1">
                      <button
                        onClick={() => toggleExpand(item.id)}
                        className="p-1.5 text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition"
                      >
                        {expandedRows.has(item.id) ? (
                          <ChevronUp size={14} />
                        ) : (
                          <ChevronDown size={14} />
                        )}
                      </button>
                    </div>
                    <div className="col-span-2">
                      <span className="font-black text-indigo-700 font-mono text-sm">
                        {item.receivingNumber}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <span className="font-bold text-slate-700 font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">
                        {item.wRNumber || "—"}
                      </span>
                    </div>
                    <div className="col-span-2 font-medium text-slate-700 text-sm">
                      {item.warehouseName || `WH #${item.warehouseId}`}
                    </div>
                    <div className="col-span-2">
                      <span className="text-sm font-semibold text-slate-700">
                        User #{item.receivedByUserId}
                      </span>
                      <p className="text-[10px] text-slate-400">
                        {formatDateShort(item.receivedDate)}
                      </p>
                    </div>
                    <div className="col-span-1">
                      <StatusBadge
                        status={item.isVoid ? "Voided" : item.status}
                      />
                    </div>
                    <div className="col-span-1 text-xs text-slate-500">
                      {formatDateShort(item.createdDate)}
                    </div>
                    <div className="col-span-1 flex justify-end">
                      {!item.isVoid && (
                        <button
                          onClick={() =>
                            setVoidTarget({
                              id: item.id,
                              receivingNumber: item.receivingNumber,
                            })
                          }
                          className="px-2 py-1 text-xs font-bold text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition flex items-center gap-1"
                          title="Void"
                        >
                          <AlertTriangle size={12} /> Void
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Mobile Card */}
                  <div className="md:hidden flex flex-col gap-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-black text-indigo-700 font-mono">
                          {item.receivingNumber}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          WR:{" "}
                          <span className="font-bold font-mono">
                            {item.wRNumber || "—"}
                          </span>
                        </p>
                      </div>
                      <StatusBadge
                        status={item.isVoid ? "Voided" : item.status}
                      />
                    </div>

                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <User size={11} /> User #{item.receivedByUserId} ·{" "}
                      {formatDateShort(item.receivedDate)}
                    </div>

                    <div className="flex items-center flex-wrap gap-2 pt-2 border-t border-slate-100">
                      <button
                        onClick={() => toggleExpand(item.id)}
                        className="flex-1 text-xs font-bold text-indigo-600 bg-indigo-50 py-1.5 rounded-lg flex items-center justify-center gap-1"
                      >
                        <Package size={11} />{" "}
                        {expandedRows.has(item.id)
                          ? "Hide Items"
                          : "View Items"}
                      </button>
                      {!item.isVoid && (
                        <button
                          onClick={() =>
                            setVoidTarget({
                              id: item.id,
                              receivingNumber: item.receivingNumber,
                            })
                          }
                          className="flex-1 text-xs font-bold text-red-500 bg-red-50 py-1.5 rounded-lg flex items-center justify-center gap-1"
                        >
                          <AlertTriangle size={11} /> Void
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Items */}
                {expandedRows.has(item.id) && (
                  <div className="bg-slate-50/60 md:border-b border-slate-100">
                    <ExpandedItems items={item.items} />
                    {item.remarks && (
                      <p className="px-6 pb-3 text-xs text-slate-500 italic">
                        📝 {item.remarks}
                      </p>
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}

            {currentItems.length === 0 && (
              <div className="text-center py-16 text-slate-400 bg-white md:bg-transparent rounded-2xl border border-slate-100 md:border-none">
                <ScanLine size={32} className="mx-auto mb-3 opacity-30" />
                <p className="font-semibold text-sm">
                  No receiving records found.
                </p>
                <p className="text-xs mt-1">
                  Create a new receiving from an approved Warehouse Request.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── PAGINATION ── */}
        <div className="mt-4 pb-6">
          <Pagination
            itemsPerPage={itemsPerPage}
            totalItems={filtered.length}
            currentPage={currentPage}
            paginate={setCurrentPage}
          />
        </div>
      </div>
    </div>
  );
};

export default AllWarehouseReceiving;
